import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import './PropertyPicker.css';

// The shared "choose what appears" popover (2026-07-26).
//
// WHY THIS MOVED OUT OF Work/. It shipped with the board card-properties work
// and was described on two cards as "presentation-agnostic, reusable as-is".
// It was not: only selected/onChange were props, while the catalog, the
// defaults, the trigger label, the popover title and the board-specific
// grouping note were all imported or hardcoded. Everything that varies between
// surfaces is now a prop, and the component knows nothing about tasks.
//
// Accessibility is modelled on SortMenu, which is the established pattern in
// this module and already re-earned the bar the native <select> gave for free.
// The difference is MULTI-select, so the roles differ deliberately:
// SortMenu is a listbox of single-choice options; this is a group of checkboxes,
// because each item is independently on or off. Using role="option" with
// aria-selected here would misdescribe it to a screen reader as a pick-one list.
//
//   • trigger: aria-haspopup="dialog" + aria-expanded
//   • popup: role="group" (labelled) containing real <input type="checkbox">
//   • keyboard: native — Tab moves, Space toggles. Esc closes and returns focus.
//   • click-outside closes; the popup does NOT close on toggle, since choosing
//     is inherently several decisions in a row.
//
// Props:
//   catalog   [{ key, label, group? }] — what can be chosen. `group` is an
//             optional heading; consecutive items sharing one render under it.
//   defaults  string[] — what Reset restores.
//   selected  string[] — current selection (controlled).
//   onChange  (next: string[]) => void
//   title     popover heading. triggerLabel / triggerIcon / triggerClassName
//             style the button. note renders a footnote when the surface has a
//             rule worth explaining in situ; omitted, nothing renders.
function PropertyPicker({
	catalog,
	defaults,
	selected,
	onChange,
	title = 'Show',
	triggerLabel = 'Properties',
	triggerIcon = 'fa-sliders',
	triggerClassName = '',
	note = '',
}) {
	const [open, setOpen] = useState(false);
	const triggerRef = useRef(null);
	const popRef = useRef(null);
	const baseId = useId();

	const close = useCallback((returnFocus = true) => {
		setOpen(false);
		if (returnFocus && triggerRef.current) triggerRef.current.focus();
	}, []);

	useEffect(() => {
		if (!open) return undefined;
		const onKey = (e) => {
			if (e.key === 'Escape') {
				e.stopPropagation();
				close();
			}
		};
		const onDown = (e) => {
			if (
				popRef.current &&
				!popRef.current.contains(e.target) &&
				!triggerRef.current.contains(e.target)
			) {
				close(false);
			}
		};
		document.addEventListener('keydown', onKey);
		document.addEventListener('pointerdown', onDown);
		return () => {
			document.removeEventListener('keydown', onKey);
			document.removeEventListener('pointerdown', onDown);
		};
	}, [open, close]);

	const toggle = (key) => {
		onChange(
			selected.includes(key)
				? selected.filter((k) => k !== key)
				: [...selected, key]
		);
	};

	const count = selected.length;

	return (
		<div className="pp-picker">
			<button
				ref={triggerRef}
				type="button"
				className={`${triggerClassName} pp-picker__trigger`.trim()}
				aria-haspopup="dialog"
				aria-expanded={open}
				onClick={() => setOpen((v) => !v)}>
				<i className={`fa-sharp fa-solid ${triggerIcon}`} />
				{triggerLabel}
				<span className="pp-picker__count">{count}</span>
			</button>

			{open && (
				<div
					ref={popRef}
					className="pp-picker__pop"
					role="group"
					aria-labelledby={`${baseId}-label`}>
					<div className="pp-picker__head">
						<span id={`${baseId}-label`} className="pp-picker__title">
							{title}
						</span>
						<button
							type="button"
							className="pp-picker__reset"
							onClick={() => onChange([...defaults])}>
							Reset
						</button>
					</div>

					<ul className="pp-picker__list">
						{catalog.map((p, i) => {
							const id = `${baseId}-${p.key}`;
							// A heading renders when this item starts a new group.
							// Comparing against the PREVIOUS item rather than tracking
							// state keeps the catalog the only source of order.
							const heading =
								p.group && p.group !== catalog[i - 1]?.group
									? p.group
									: null;
							return (
								<React.Fragment key={p.key}>
									{heading && (
										// Deliberately NOT aria-hidden: it is the
										// only thing telling a screen-reader user
										// that "Today" sits under Daily Operations
										// and not under Setup & Data.
										<li className="pp-picker__group">
											{heading}
										</li>
									)}
									<li>
										<input
											id={id}
											type="checkbox"
											checked={selected.includes(p.key)}
											onChange={() => toggle(p.key)}
										/>
										<label htmlFor={id}>{p.label}</label>
									</li>
								</React.Fragment>
							);
						})}
					</ul>

					{note && <p className="pp-picker__note">{note}</p>}
				</div>
			)}
		</div>
	);
}

export default PropertyPicker;
